"use client";

import dynamic from "next/dynamic";
import { useEffect, useState } from "react";

import type { ToastEvent } from "./toaster/types";

const toastEvents = {
  listeners: new Set<(event: ToastEvent) => void>(),
  emit(event: ToastEvent) {
    this.listeners.forEach(listener => listener(event));
  },
  subscribe(listener: (event: ToastEvent) => void) {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  },
};

// Dynamically import the Toaster component
const DynamicToaster = dynamic(
  () => import("./toaster/ToasterComponent").then(mod => ({ default: mod.ToasterComponent })),
  {
    ssr: false,
    loading: () => null,
  },
);

// Component that handles all toast logic
export const LazyToaster = () => {
  const [shouldRender, setShouldRender] = useState(false);
  const [toastQueue, setToastQueue] = useState<ToastEvent[]>([]);

  useEffect(() => {
    // Subscribe to toast events
    const unsubscribe = toastEvents.subscribe(event => {
      // Add event to queue and trigger rendering
      setToastQueue(prev => [...prev, event]);
      setShouldRender(true);
    });

    return unsubscribe;
  }, []);

  if (!shouldRender) {
    return null;
  }

  // Render the dynamically imported Toaster component and pass the toast queue
  return <DynamicToaster toastQueue={toastQueue} />;
};

// Simple toast object that emits events
export const toast = {
  success(message: string, options?: ToastEvent["options"]) {
    toastEvents.emit({ type: "success", message, options });
  },

  error(message: string, options?: ToastEvent["options"]) {
    toastEvents.emit({ type: "error", message, options });
  },

  warning(message: string, options?: ToastEvent["options"]) {
    toastEvents.emit({ type: "warning", message, options });
  },

  info(message: string, options?: ToastEvent["options"]) {
    toastEvents.emit({ type: "info", message, options });
  },

  promise(promise: Promise<any>, options?: any) {
    toastEvents.emit({ type: "promise", message: promise, options });
  },

  loading(message: string, options?: ToastEvent["options"]) {
    toastEvents.emit({ type: "loading", message, options });
  },
};
